import { useState, useEffect } from 'react';
import { X, Star } from 'lucide-react';
import api from '../../lib/api';
import toast from 'react-hot-toast';
import { PersonDedupeCheck, type PersonMatch } from './PersonDedupeCheck';
import type { SportWithTeams, Gender, Season, PersonDetail } from '../../types';
import { formatSportLabel } from '../../lib/sportLabel';
import { compareLevels } from '../../lib/levelRank';

interface AddPersonModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
  sports: SportWithTeams[];
  preSelectedSportId?: string | null;
  preSelectedTeamId?: string | null;
  /** Called after a successful add OR link. The parent refetches the org. */
  onSuccess: () => void;
}

const ROLE_SUGGESTIONS = [
  'Head Coach',
  'Assistant Coach',
  'Athletic Director',
  'Associate AD',
  'Coordinator',
  'Booster Club President',
  'Team Manager',
];

const seasonLabel = (season: Season): string => {
  switch (season) {
    case 'FALL': return 'Fall';
    case 'WINTER': return 'Winter';
    case 'SPRING': return 'Spring';
    case 'SUMMER': return 'Summer';
  }
};

type Mode = 'create' | 'link';

/**
 * Add Person modal — supersedes LinkPersonModal across the Org page.
 *
 * Modes:
 *   - create: rep types a new person, server creates Person +
 *     PersonSportTeam atomically via POST /api/people/with-link.
 *   - link: rep picked an existing match from PersonDedupeCheck;
 *     modal switches into a confirmation view and POSTs to
 *     /api/people/:personId/organizations.
 */
export const AddPersonModal = ({
  isOpen,
  onClose,
  organizationId,
  organizationName,
  sports,
  preSelectedSportId = null,
  preSelectedTeamId = null,
  onSuccess,
}: AddPersonModalProps) => {
  const [mode, setMode] = useState<Mode>('create');

  // create-mode form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('');
  const [selectedSportId, setSelectedSportId] = useState<string>('');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [isPrimary, setIsPrimary] = useState(true);

  // link-mode state
  const [linkedPerson, setLinkedPerson] = useState<PersonDetail | null>(null);
  const [linkRole, setLinkRole] = useState('');
  const [linkIsPrimary, setLinkIsPrimary] = useState(false);

  // shared
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateExistingId, setDuplicateExistingId] = useState<string | null>(null);

  // Reset state when the modal opens; apply pre-selections.
  useEffect(() => {
    if (isOpen) {
      setMode('create');
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setRole('');
      setSelectedSportId(preSelectedSportId || '');
      setSelectedTeamId(preSelectedTeamId || '');
      setIsPrimary(true);
      setLinkedPerson(null);
      setLinkRole('');
      setLinkIsPrimary(false);
      setError(null);
      setDuplicateExistingId(null);
      setIsSaving(false);
    }
  }, [isOpen, preSelectedSportId, preSelectedTeamId]);

  if (!isOpen) return null;

  const activeSports = sports.filter((s) => s.isActive);
  const selectedSport = activeSports.find((s) => s.id === selectedSportId);
  const availableTeams = (selectedSport?.teams || [])
    .filter((t) => t.isActive)
    .sort((a, b) => compareLevels(a.level, b.level));
  // Lock the Sport field when the modal was opened from a specific sport section
  // (and that sport resolves to an active sport). If a stale/inactive id was
  // passed and doesn't resolve, leave the field editable so the rep isn't stuck.
  const lockSport = !!preSelectedSportId && !!selectedSport;

  // ── State 3 transition: rep tapped a match. Fetch the full person
  // so we have their primary-org context for the confirmation card.
  const handleMatchSelected = async (match: PersonMatch) => {
    setError(null);
    setIsSaving(true);
    try {
      const response = await api.get(`/api/people/${match.id}`);
      const full = response.data.person as PersonDetail;
      setLinkedPerson(full);
      setLinkRole('');
      setLinkIsPrimary(false);
      setMode('link');
    } catch {
      setError('Could not load that person. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackToSearch = () => {
    setMode('create');
    setLinkedPerson(null);
    setLinkRole('');
    setLinkIsPrimary(false);
    setError(null);
  };

  const handleSportChange = (v: string) => {
    setSelectedSportId(v);
    setSelectedTeamId('');
  };

  // ── Create branch ────────────────────────────────────────────
  const isCreateValid = (): boolean => {
    if (!firstName.trim() || !lastName.trim()) return false;
    if (!email.trim() && !phone.trim()) return false;
    return true;
  };

  const handleSubmitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isCreateValid()) return;
    setIsSaving(true);
    setError(null);
    setDuplicateExistingId(null);
    try {
      const response = await api.post('/api/people/with-link', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        role: role.trim() || null,
        organizationId,
        sportId: selectedSportId || null,
        teamId: selectedTeamId || null,
        isPrimary,
      });
      const personName = `${response.data.person.firstName} ${response.data.person.lastName}`;
      toast.success(`Added ${personName} to ${organizationName}`);
      onSuccess();
      onClose();
    } catch (err: any) {
      if (err.response?.status === 409) {
        // Server says this email belongs to an existing person — give
        // the rep an explicit recovery path.
        const existingId = err.response?.data?.existingPersonId;
        setError('That email is already used by another person.');
        if (existingId) setDuplicateExistingId(existingId);
      } else {
        setError(err.response?.data?.message || 'Failed to add person');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewExisting = async () => {
    if (!duplicateExistingId) return;
    // Switch into link mode with the existing person (fetch full record).
    setIsSaving(true);
    try {
      const response = await api.get(`/api/people/${duplicateExistingId}`);
      setLinkedPerson(response.data.person as PersonDetail);
      setLinkRole('');
      setLinkIsPrimary(false);
      setError(null);
      setDuplicateExistingId(null);
      setMode('link');
    } catch {
      setError('Could not load that person. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Link branch ──────────────────────────────────────────────
  const handleSubmitLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkedPerson) return;
    setIsSaving(true);
    setError(null);
    try {
      await api.post(`/api/people/${linkedPerson.id}/organizations`, {
        organizationId,
        sportId: selectedSportId || null,
        teamId: selectedTeamId || null,
        role: linkRole.trim() || null,
        isPrimary: linkIsPrimary,
      });
      const personName = `${linkedPerson.firstName} ${linkedPerson.lastName}`;
      toast.success(`Linked ${personName} to ${organizationName}`);
      onSuccess();
      onClose();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to link person';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  // Build the context label (where we're adding/linking)
  let contextLabel: string = organizationName;
  if (selectedSport) {
    contextLabel = `${organizationName} · ${formatSportLabel(selectedSport.gender, selectedSport.name)}`;
    const team = availableTeams.find((t) => t.id === selectedTeamId);
    if (team) contextLabel += ` · ${team.level}`;
  }

  // For State 3, identify the existing person's current primary org (if any)
  const existingPrimary = linkedPerson?.organizations?.find((o) => o.isPrimary);
  const existingPrimaryName = existingPrimary?.organization?.name;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">

          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {mode === 'create' ? 'Add Person' : 'Link Existing Person'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Context line */}
          <div className="px-6 py-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            {mode === 'create' ? 'Adding to' : 'Linking to'}:{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">{contextLabel}</span>
          </div>

          {/* ── CREATE MODE ── */}
          {mode === 'create' && (
            <form onSubmit={handleSubmitCreate}>
              <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
                {error && (
                  <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-300 text-sm flex items-start justify-between gap-2">
                    <span>{error}</span>
                    {duplicateExistingId && (
                      <button
                        type="button"
                        onClick={handleViewExisting}
                        className="flex-shrink-0 text-xs font-semibold underline whitespace-nowrap"
                      >
                        Use existing
                      </button>
                    )}
                  </div>
                )}

                <PersonDedupeCheck
                  firstName={firstName}
                  lastName={lastName}
                  email={email}
                  onFirstNameChange={setFirstName}
                  onLastNameChange={setLastName}
                  onEmailChange={setEmail}
                  onMatchSelected={handleMatchSelected}
                  disabled={isSaving}
                />

                <div>
                  <label htmlFor="add-person-phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="add-person-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={isSaving}
                    className="input"
                    placeholder="555-123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="add-person-role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Role
                  </label>
                  <input
                    type="text"
                    id="add-person-role"
                    list="add-person-role-suggestions"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    disabled={isSaving}
                    className="input"
                    placeholder="e.g., Assistant Coach"
                  />
                  <datalist id="add-person-role-suggestions">
                    {ROLE_SUGGESTIONS.map((r) => (
                      <option key={r} value={r} />
                    ))}
                  </datalist>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="add-person-sport" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Sport {!lockSport && <span className="text-xs text-gray-400">(optional)</span>}
                    </label>
                    <select
                      id="add-person-sport"
                      value={selectedSportId}
                      onChange={(e) => handleSportChange(e.target.value)}
                      disabled={isSaving || lockSport}
                      className="input"
                    >
                      {!lockSport && <option value="">-- Org level --</option>}
                      {activeSports.map((s) => (
                        <option key={s.id} value={s.id}>
                          {formatSportLabel(s.gender, s.name)} ({seasonLabel(s.season)})
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedSportId && (
                    <div>
                      <label htmlFor="add-person-team" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Team <span className="text-xs text-gray-400">(optional)</span>
                      </label>
                      <select
                        id="add-person-team"
                        value={selectedTeamId}
                        onChange={(e) => setSelectedTeamId(e.target.value)}
                        disabled={isSaving}
                        className="input"
                      >
                        <option value="">-- Sport level --</option>
                        {availableTeams.map((t) => (
                          <option key={t.id} value={t.id}>{t.level}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="add-person-isPrimary"
                    checked={isPrimary}
                    onChange={(e) => setIsPrimary(e.target.checked)}
                    disabled={isSaving}
                    className="h-4 w-4 text-blast-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blast-500"
                  />
                  <label htmlFor="add-person-isPrimary" className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1">
                    <Star className="h-3.5 w-3.5 text-amber-500" />
                    Set as primary org for this person
                  </label>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end space-x-3">
                <button type="button" onClick={onClose} className="btn-secondary" disabled={isSaving}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={isSaving || !isCreateValid()}>
                  {isSaving ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Adding…
                    </>
                  ) : 'Add'}
                </button>
              </div>
            </form>
          )}

          {/* ── LINK MODE (State 3) ── */}
          {mode === 'link' && linkedPerson && (
            <form onSubmit={handleSubmitLink}>
              <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
                {error && (
                  <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-300 text-sm">
                    {error}
                  </div>
                )}

                <div className="bg-blast-50 dark:bg-blast-900/20 border border-blast-200 dark:border-blast-800/50 rounded-lg p-4 flex items-center gap-3">
                  <div className="flex-shrink-0 h-10 w-10 bg-blast-100 dark:bg-blast-900/40 rounded-full flex items-center justify-center text-blast-700 dark:text-blast-300 font-semibold text-sm">
                    {linkedPerson.firstName[0]}{linkedPerson.lastName[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-gray-900 dark:text-gray-100">
                      {linkedPerson.firstName} {linkedPerson.lastName}
                    </div>
                    {linkedPerson.email && (
                      <div className="text-sm text-gray-600 dark:text-gray-400 truncate">{linkedPerson.email}</div>
                    )}
                    {existingPrimary && (
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                        {existingPrimary.role && <>{existingPrimary.role} · </>}
                        {existingPrimary.organization.name}
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Not the right person?{' '}
                  <button type="button" onClick={handleBackToSearch} className="text-blast-600 dark:text-blast-400 hover:underline">
                    Back to search
                  </button>
                </p>

                <div>
                  <label htmlFor="link-role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Role at {contextLabel}
                  </label>
                  <input
                    type="text"
                    id="link-role"
                    list="link-role-suggestions"
                    value={linkRole}
                    onChange={(e) => setLinkRole(e.target.value)}
                    disabled={isSaving}
                    className="input"
                    placeholder="e.g., Head Coach"
                  />
                  <datalist id="link-role-suggestions">
                    {ROLE_SUGGESTIONS.map((r) => (
                      <option key={r} value={r} />
                    ))}
                  </datalist>
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="link-isPrimary"
                      checked={linkIsPrimary}
                      onChange={(e) => setLinkIsPrimary(e.target.checked)}
                      disabled={isSaving}
                      className="h-4 w-4 text-blast-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blast-500"
                    />
                    <label htmlFor="link-isPrimary" className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1">
                      <Star className="h-3.5 w-3.5 text-amber-500" />
                      Set as primary org for this person
                    </label>
                  </div>
                  {existingPrimaryName && (
                    <p className="text-xs text-gray-500 dark:text-gray-500 ml-6">
                      They're already primary at {existingPrimaryName}.
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end space-x-3">
                <button type="button" onClick={onClose} className="btn-secondary" disabled={isSaving}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Linking…
                    </>
                  ) : 'Link'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
